/* eslint-disable no-nested-ternary */
import lod_ from "lodash";
import { useEffect, useState, memo, useCallback } from "react";
import { Select, MenuItem, FormControl, Icon, Checkbox, Switch, Box, Tooltip } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import MDTypography from "components/Basics/MDTypography";
import MDInput from "components/Basics/MDInput";

export const ALLOWED_FORMATS_FOR_HTML_SELECTOR = ["WebPage"];
export const ALLOWED_FORMATS_FOR_OCR = ["documents", "WebPage"];

// Styled row component (same design as AssetTable)
const TableRow = styled(Box)(({ theme, selected, index }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(0.75, 1.5),
	borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
	cursor: "pointer",
	transition: "all 0.15s ease",
	backgroundColor: selected
		? alpha(theme.palette.info.main, 0.08)
		: index % 2 === 1
			? alpha(theme.palette.grey[600], 0.05)
			: "transparent",
	height: "100%",
	"&:hover": {
		backgroundColor: selected ? alpha(theme.palette.info.main, 0.12) : theme.palette.action.hover
	}
}));

// Column widths (matching header)
const COLUMNS = {
	checkbox: 50,
	name: "1.5fr",
	language: 100,
	htmlSelector: "1fr",
	ocr: 120,
	url: "1.5fr"
};

/**
 * Row item for each file in the list
 * @param {*} param0
 * @returns {JSX.Element} - RowItem
 */
const RowItem = ({
	file,
	isSelected,
	onToggleSelection,
	onChange,
	languages = [],
	languagesLoading,
	index,
	format
}) => {
	const [loading, setLoading] = useState(true);

	/**
	 * Define a local state to manage the inputs
	 */
	const [localState, setLocalState] = useState({
		language: "",
		htmlSelector: "",
		extractTextFromImage: false
	});

	/**
	 * Handle click on URL to open in new tab
	 */
	const handleUrlClick = () => {
		window.open(file.url, "_blank", "noopener,noreferrer");
	};

	/**
	 * Handle selection toggle
	 */
	const handleSelectionToggle = useCallback(() => {
		onToggleSelection(file.url);
	}, [file.url, onToggleSelection]);

	/**
	 * Handle change for each input
	 * @param {string} key - Key to change
	 * @param {any} value - New value
	 */
	const onChangeHandle = (key, value) => {
		onChange(file.url, key, value);
	};

	useEffect(() => {
		// If languages are still loading, do nothing
		if (languagesLoading) {
			return;
		}

		const defaultLocalState = {
			language: file.language || "",
			htmlSelector: file.metadatas?.htmlSelector || "",
			extractTextFromImage: file.metadatas?.extractTextFromImage || false
		};

		setLoading(true);
		const defaultStateLanguage = defaultLocalState.language;

		if (lod_.isNil(defaultStateLanguage) || lod_.isEmpty(defaultStateLanguage)) {
			// If no language is set, try to set the default language
			const defaultLanguage = languages.find(lang => lang.default === true);
			onChangeHandle("language", defaultLanguage?.code || "");
			setLocalState(prev => ({ ...prev, language: defaultLanguage?.code || "" }));
		} else {
			setLocalState(defaultLocalState);
		}

		setLoading(false);
	}, [languagesLoading, file]);

	if (loading) {
		return null;
	}

	return (
		<TableRow selected={isSelected} index={index} onClick={handleSelectionToggle}>
			{/* Checkbox */}
			<Box sx={{ width: COLUMNS.checkbox, flexShrink: 0 }}>
				<Checkbox checked={isSelected} size="small" sx={{ p: 0.5 }} />
			</Box>

			{/* File name */}
			<Box
				sx={{
					flex: 1.5,
					display: "flex",
					alignItems: "center",
					minWidth: 0,
					pr: 1.5
				}}
			>
				<Tooltip title={file.name} placement="top-start">
					<MDTypography
						fontWeight="bold"
						variant="button"
						color="info"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%"
						}}
					>
						{file.name}
					</MDTypography>
				</Tooltip>
			</Box>

			{/* Language selector */}
			<Box sx={{ width: COLUMNS.language, flexShrink: 0 }}>
				{!languagesLoading && (
					<FormControl
						size="small"
						sx={{ minWidth: 80, width: "100%" }}
						onClick={e => e.stopPropagation()}
					>
						<Select
							value={localState.language}
							onChange={e => {
								const newLanguage = e.target.value;
								setLocalState(prev => ({ ...prev, language: newLanguage }));
								onChangeHandle("language", newLanguage);
							}}
							onOpen={e => e.stopPropagation()}
							onClose={e => e.stopPropagation()}
							displayEmpty
							sx={{
								fontSize: "0.75rem",
								"& .MuiSelect-select": {
									padding: "4px 8px"
								},
								"& fieldset": {
									backgroundColor: "white.main"
								}
							}}
							renderValue={selected => {
								if (!selected) {
									return <MDTypography color="text">-</MDTypography>;
								}
								const lang = languages.find(l => l.code === selected);
								return (
									<Box display="flex" alignItems="center" justifyContent="space-between">
										{lang?.logoURL && (
											<img
												src={lang.logoURL}
												alt={lang.name}
												style={{ width: 16, height: 12, marginRight: 4, zIndex: 4000 }}
											/>
										)}
										<MDTypography
											variant="override"
											color="black"
											sx={{ zIndex: 4000, fontSize: "0.75rem" }}
										>
											{lang ? lang.code : selected}
										</MDTypography>
										<Icon sx={{ pl: 1, zIndex: 4000, fontSize: "1rem" }} color="black">
											expand_more
										</Icon>
									</Box>
								);
							}}
						>
							{languages.map(language => (
								<MenuItem key={language.code} value={language.code} sx={{ fontSize: "0.75rem" }}>
									<Box display="flex" alignItems="center">
										{language.logoURL && (
											<img
												src={language.logoURL}
												alt={language.name}
												style={{ width: 16, height: 12, marginRight: 8 }}
											/>
										)}
										{language.name}
									</Box>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
			</Box>

			{/* HTML Selector input */}
			{ALLOWED_FORMATS_FOR_HTML_SELECTOR.includes(format) && (
				<Box sx={{ flex: 1, px: 1 }}>
					<MDInput
						value={localState.htmlSelector}
						placeholder="Ex: article.content"
						fullWidth
						sx={{
							"& .MuiInputBase-input": {
								padding: "4px 8px",
								fontSize: "0.75rem",
								fontWeight: "500"
							},
							"& .MuiInputBase-root": {
								backgroundColor: "white !important"
							}
						}}
						onClick={e => e.stopPropagation()}
						onChange={e => {
							const newValue = e.target.value;
							setLocalState(prev => ({ ...prev, htmlSelector: newValue }));
						}}
						onBlur={e => {
							const newValue = e.target.value;
							onChangeHandle("metadatas.htmlSelector", newValue);
						}}
					/>
				</Box>
			)}

			{/* Extract text from image */}
			{ALLOWED_FORMATS_FOR_OCR.includes(format) && (
				<Box
					sx={{
						width: COLUMNS.ocr,
						flexShrink: 0,
						display: "flex",
						justifyContent: "center"
					}}
				>
					<Switch
						checked={localState.extractTextFromImage}
						onClick={e => e.stopPropagation()}
						onChange={e => {
							const newValue = e.target.checked;
							setLocalState(prev => ({ ...prev, extractTextFromImage: newValue }));
							onChangeHandle("metadatas.extractTextFromImage", newValue);
						}}
						color="info"
					/>
				</Box>
			)}

			{/* URL */}
			<Box
				sx={{
					flex: 1.5,
					display: "flex",
					alignItems: "center",
					minWidth: 0,
					pl: 1.5
				}}
			>
				{file?.url?.startsWith("http") ? (
					<Tooltip title={file.url} placement="top-start">
						<MDTypography
							variant="caption"
							color="info"
							sx={{
								textDecoration: "underline",
								cursor: "pointer",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%"
							}}
							onClick={handleUrlClick}
						>
							{file.url}
						</MDTypography>
					</Tooltip>
				) : (
					<MDTypography
						variant="caption"
						color="text"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%"
						}}
					>
						-
					</MDTypography>
				)}
			</Box>
		</TableRow>
	);
};

export default memo(RowItem, (prevProps, nextProps) => {
	return (
		prevProps.isSelected === nextProps.isSelected &&
		lod_.isEqual(prevProps.file, nextProps.file) &&
		lod_.isEqual(prevProps.languages, nextProps.languages) &&
		prevProps.languagesLoading === nextProps.languagesLoading &&
		prevProps.index === nextProps.index &&
		prevProps.format === nextProps.format
	);
});
